import {  useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountId } from '../../../auth/useAccountId';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';

import Page from '../../../components/Page';
import Card from '../../../components/Card';
import Paging from '../../../components/Paging/Infinite';
import { State } from '../../../reducer';
import namespace from '../namespace';
import { LoadingOverlay } from '../../../components/Loading';
import ReportsTable from './ReportsTable';
import NoData from './NoData';
import { useActions } from '../../../Actions';
import useEffectCallback from '../../../util/useEffectCallback';
import { ReportMetadata } from '../../../types/reports';
import startOfDay from 'date-fns/startOfDay';
import { parseISOStrictOr } from '../../../helpers/date';

type ReportGroup = ReportMetadata[];

export type MonthlyReportGroups = {
    month: string;
    groups: ReportGroup[];
};

const toGroupByKey = (report: ReportMetadata) => {
    return [report.data_from, report.data_to, report.schedule, report.data_type].join('-');
};

const fallbackDate = new Date(0);

const cmpDate = (a: ReportMetadata | undefined, b: ReportMetadata | undefined) => {
    const aDate = startOfDay(parseISOStrictOr(a?.data_from, fallbackDate));
    const bDate = startOfDay(parseISOStrictOr(b?.data_from, fallbackDate));
    return bDate.valueOf() - aDate.valueOf();
};

const cmpGroup = (a: ReportGroup, b: ReportGroup) => {
    const aFirstChild = a.find((report) => report);
    const bFirstChild = b.find((report) => report);
    return cmpDate(aFirstChild, bFirstChild);
};

const groupByAttributes = (reports: ReportGroup) => {
    // assumes input is a sorted list with the most recent reports first
    const grouped = reports.reduce<{ [key: string]: ReportMetadata[] }>((acc, report) => {
        const key = toGroupByKey(report);
        return {
            ...acc,
            [key]: [...(acc[key] || []), report],
        };
    }, {});
    const sortedGroups = Object.values(grouped).sort(cmpGroup);
    return sortedGroups;
};

const findLastReportGroupMonth = (reportGroupGroups: MonthlyReportGroups[]) => {
    if (reportGroupGroups.length === 0) {
        return undefined;
    }
    const lastGroup = reportGroupGroups[reportGroupGroups.length - 1];
    return lastGroup?.month;
};

const getGroupMonth = (group: ReportGroup) => {
    const firstFromDate = group.find((report) => report)?.data_from;
    return firstFromDate ? firstFromDate.substr(0, 7) : undefined;
};

const toMonthlyGroups = (groups: ReportGroup[]) => {
    const months = groups.reduce<MonthlyReportGroups[]>((reduced, group) => {
        const lastGroupMonth = findLastReportGroupMonth(reduced);
        const currentGroupMonth = getGroupMonth(group);
        if (currentGroupMonth && currentGroupMonth === lastGroupMonth) {
            // append to last MonthReportGroupGroups
            const lastMonthGroup = reduced[reduced.length - 1];
            if (lastMonthGroup) {
                lastMonthGroup.groups.push(group);
                return reduced;
            }
        }
        if (currentGroupMonth) {
            const monthlyGroup = {
                month: currentGroupMonth,
                groups: [group],
            };
            return [...reduced, monthlyGroup];
        }
        return reduced;
    }, []);
    return months;
};

const filterReportsByPagingCutoff = (reports: ReportMetadata[], pagingCutoff: Date | undefined) => {
    if (!pagingCutoff) {
        return reports;
    }
    return reports.filter((report) => {
        return parseISOStrictOr(report.data_from, fallbackDate) > pagingCutoff;
    });
};

const nextParams = (params: URLSearchParams, starting_after: string | undefined) => {
    const next = new URLSearchParams(params);
    next.delete('starting_after');
    if (starting_after) {
        next.append('starting_after', starting_after);
    }
    return next;
};

const ReportList = () => {
    const actions = useActions(namespace);
    const { t } = useTranslation();
    const accountId = useAccountId();
    const { reports, isLoading, starting_after, params, pagingCutoff } = useSelector<State, State[typeof namespace]>(
        (s) => s[namespace]
    );
    const filteredByCutoff = useMemo(() => filterReportsByPagingCutoff(reports, pagingCutoff), [reports, pagingCutoff]);
    const monthlyGroups = useMemo(() => {
        const reportGroups = groupByAttributes(filteredByCutoff);
        return toMonthlyGroups(reportGroups);
    }, [filteredByCutoff]);
    useEffectCallback(actions.fetchReports, [accountId, params]);
    return (
        <Page title={t('reports.report_list.title')} hasEnvFrame={true}>
            <Card noPadding>
                <ReportsTable accountId={accountId} monthlyGroups={monthlyGroups} />
                <Wrapper>
                    <Paging
                        label={t('reports.report_list.load_more')}
                        isLoading={isLoading}
                        hasMore={starting_after !== undefined || reports.length > filteredByCutoff.length}
                        onLoadMore={() => {
                            if (starting_after) {
                                actions.updateParams(nextParams(params, starting_after));
                            } else if (!starting_after && pagingCutoff) {
                                // reset paging cutoff so all the reports filtered by cutoff are listed
                                actions.updatePagingCutoff(undefined);
                            }
                        }}
                    />
                    {isLoading && <LoadingOverlay />}
                </Wrapper>
                {!isLoading && reports.length === 0 && <NoData />}
            </Card>
        </Page>
    );
};

const Wrapper = styled.div`
    position: relative;
`;

export default ReportList;
